import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { formatPercent, HeroAvatar, useHeroLookup } from "@/features/matches";
import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import { AXIS_LABELS } from "../../model/AXIS_LABELS";
import { commonHeroes } from "../../model/commonHeroes";
import { duelNumbers, duelScore, duelSides } from "../../model/duel";
import type { FriendPlayer, StatAxis } from "../../model/FriendsBoard";
import { formatMetric } from "../../model/formatMetric";
import { METRIC_LABELS } from "../../model/METRIC_LABELS";
import { DuelBars } from "./DuelBars";
import { SidesSquare } from "./SidesSquare";

const YOU_VS = msg({ message: "You vs {name}" });
const AHEAD = msg({ message: "You're ahead in {points} of {total}. Something to brag about." });
const BEHIND = msg({
  message: "{name} is ahead in {points} of {total}. See where the gap is biggest.",
});
const DRAW = msg({ message: "A draw: {points} each." });
const SCORE = msg({ message: "Score: you {me}, {name} {them}" });
const GAMES = msg({
  message:
    "you: {mine, plural, one {# match} other {# matches}}, friend: {theirs, plural, one {# match} other {# matches}}",
});
const WINS_OF = msg({
  message: "{wins, plural, one {# win} other {# wins}} of {matches}",
});

type DuelPanelProps = {
  id: string;
  me: FriendPlayer;
  them: FriendPlayer;
  onClose: () => void;
};

// Один на один: квадрат сторон игры, строки сравнения, общие герои и матчи вместе.
export const DuelPanel = ({ id, me, them, onClose }: DuelPanelProps) => {
  const { i18n, t } = useLingui();
  const heroLookup = useHeroLookup();
  const [hovered, setHovered] = useState<StatAxis | null>(null);
  const name = them.display_name ?? t`Friend`;
  const sides = duelSides(me.current, them.current);
  const numbers = duelNumbers(me.current, them.current);
  const score = duelScore([...sides, ...numbers]);
  const total = sides.length + numbers.length;
  let verdict = i18n._({ ...DRAW, values: { points: score.me } });
  if (score.me > score.them) {
    verdict = i18n._({ ...AHEAD, values: { points: score.me, total } });
  } else if (score.me < score.them) {
    verdict = i18n._({ ...BEHIND, values: { name, points: score.them, total } });
  }
  const common = commonHeroes(me.heroes, them.heroes);
  const together = them.together;
  const sideText = (_key: StatAxis, value: number | null) => (value === null ? "—" : String(value));

  const tiles = [];
  if (together !== null && together.matches > 0) {
    const soloMatches = me.current.matches - together.matches;
    const soloWins = me.current.wins - together.wins;
    const withRate = together.wins / together.matches;
    tiles.push(
      { label: t`Matches together`, value: String(together.matches), sub: t`on the same team` },
      {
        label: t`Win rate together`,
        value: formatPercent(withRate, i18n.locale),
        sub: i18n._({ ...WINS_OF, values: { wins: together.wins, matches: together.matches } }),
      },
    );
    if (soloMatches > 0) {
      const soloRate = soloWins / soloMatches;
      tiles.push({
        label: t`Your win rate without them`,
        value: formatPercent(soloRate, i18n.locale),
        sub: withRate > soloRate ? t`you play better together` : t`together is worse so far`,
      });
    }
  } else {
    tiles.push({
      label: t`Matches together`,
      value: "0",
      sub: t`no matches on the same team in this period`,
    });
  }

  return (
    <div
      id={id}
      className="flex flex-wrap items-start gap-6 rounded-xl border border-border-strong bg-sidebar p-5"
    >
      <div className="flex max-w-[400px] flex-[1_1_320px] flex-col items-center gap-2.5">
        <SidesSquare sides={sides} hovered={hovered} onHover={setHovered} />
        <div className="flex gap-4 text-[13px] text-muted">
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="size-3 rounded-sm bg-accent" />
            <Trans>You</Trans>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="size-3 rounded-sm bg-rival" />
            {name}
          </span>
        </div>
        <p className="max-w-[360px] text-center text-xs text-subtle">
          <Trans>
            Scores 0–99: how many players of their own rank on the same hero each of you beats.
          </Trans>
        </p>
      </div>

      <div className="flex min-w-0 flex-[999_1_340px] flex-col gap-3.5">
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex flex-col">
            <h3 className="font-display text-2xl font-bold">
              {i18n._({ ...YOU_VS, values: { name } })}
            </h3>
            <p className="text-sm text-muted">{verdict}</p>
          </div>
          <p
            aria-label={i18n._({ ...SCORE, values: { me: score.me, them: score.them, name } })}
            className="font-mono text-[34px] font-semibold whitespace-nowrap"
          >
            <span className="text-accent">{score.me}</span>
            <span className="text-neutral"> : </span>
            <span className="text-rival">{score.them}</span>
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <h4 className="text-xs tracking-[0.06em] text-subtle uppercase">
            <Trans>Sides of the game</Trans>
          </h4>
          <DuelBars
            lines={sides}
            label={(axis) => i18n._(AXIS_LABELS[axis])}
            format={sideText}
            hovered={hovered}
            onHover={setHovered}
          />
          <h4 className="mt-1.5 text-xs tracking-[0.06em] text-subtle uppercase">
            <Trans>Match history</Trans>
          </h4>
          <DuelBars
            lines={numbers}
            label={(metric) => i18n._(METRIC_LABELS[metric])}
            format={(metric, value) => formatMetric(metric, value, i18n.locale)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="text-xs tracking-[0.06em] text-subtle uppercase">
            <Trans>Common heroes</Trans>
          </h4>
          {common.length === 0 ? (
            <p className="text-sm text-subtle">
              <Trans>You didn't play the same heroes in this period.</Trans>
            </p>
          ) : (
            <ul className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-2">
              {common.map((hero) => {
                const view = heroLookup(hero.heroId);
                const mine = hero.me.score;
                const theirs = hero.them.score;
                return (
                  <li
                    key={hero.heroId}
                    className="flex items-center gap-2.5 rounded-[10px] border border-border bg-surface px-3 py-2"
                  >
                    <HeroAvatar name={view.name} iconUrl={view.iconUrl} size={36} />
                    <span className="flex min-w-0 flex-auto flex-col">
                      <span className="font-semibold">{view.name}</span>
                      <span className="text-xs text-subtle">
                        {i18n._({
                          ...GAMES,
                          values: { mine: hero.me.matches, theirs: hero.them.matches },
                        })}
                      </span>
                    </span>
                    {(mine !== null || theirs !== null) && (
                      <span className="font-mono text-lg font-semibold whitespace-nowrap">
                        <span
                          className={cn(
                            mine !== null && (theirs === null || mine >= theirs)
                              ? "text-accent"
                              : "text-subtle",
                          )}
                        >
                          {mine ?? "—"}
                        </span>
                        <span className="text-neutral"> : </span>
                        <span
                          className={cn(
                            theirs !== null && (mine === null || theirs >= mine)
                              ? "text-rival"
                              : "text-subtle",
                          )}
                        >
                          {theirs ?? "—"}
                        </span>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <dl className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2.5">
          {tiles.map((tile) => (
            <div
              key={tile.label}
              className="flex flex-col rounded-[10px] border border-border bg-surface px-3 py-2.5"
            >
              <dt className="text-xs text-subtle">{tile.label}</dt>
              <dd className="font-mono text-xl font-semibold">{tile.value}</dd>
              <dd className="text-xs text-muted">{tile.sub}</dd>
            </div>
          ))}
        </dl>

        <div className="flex flex-wrap gap-2.5">
          {together !== null && together.last_match_id !== null && (
            <Button variant="outline" asChild>
              <Link to="/matches/$matchId" params={{ matchId: together.last_match_id }}>
                <Trans>Last match together</Trans>
              </Link>
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            <Trans>Collapse</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
};
